/*
 * pv-artist-typeahead.js
 * ----------------------
 * Powers the searchable Artist filter on collections that have a precomputed
 * `custom.artist_facet` JSON metafield (written nightly by the ARTIST-FACET job).
 * It replaces the artist <select> — which Shopify caps at 100 values — with a
 * search-as-you-type combobox built from the FULL artist list (hundreds of names).
 *
 * WHY EVENT DELEGATION: the collection filters render inside <template> elements
 * that the theme's `filters-submit` custom element clones into the live DOM with
 * JavaScript. So the type-ahead markup does NOT exist at page load and may be
 * cloned more than once. Instead of wiring each instance on load, we attach ONE
 * set of listeners to `document` and find the relevant instance per interaction.
 * This works no matter when/where the markup appears.
 *
 * SELECTION = NAVIGATION: selecting an artist navigates to the current URL with
 * `filter.p.m.custom.main_artist=<gid>` set, preserving all other active filters
 * (Format, Year) already present in the query string. Clearing removes the param.
 * (Verified live: the filter value is the bare metaobject GID, and other filters
 * persist in the URL.)
 *
 * Loaded once from collection-filters-top.liquid (which is in the live DOM). The
 * guard below makes a second include harmless.
 *
 * Markup it expects (rendered by collection-filters-display-list.liquid):
 *   <div data-artist-typeahead data-ta-param="filter.p.m.custom.main_artist">
 *     <input data-ta-input ...>
 *     <button data-ta-clear hidden ...>          (shown by JS when a filter is active)
 *     <ul data-ta-list ...></ul>                 (results; populated by JS)
 *     <script type="application/json" data-ta-data>[{name,gid,count}, ...]</script>
 *   </div>
 */
(function () {
  if (window.__pvArtistTypeahead) return;   // load-once guard (safe if included twice)
  window.__pvArtistTypeahead = true;

  var MAX_RESULTS = 1000;   // cap rendered rows for performance; rest behind "keep typing"

  /* ----- helpers ----- */
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }

  function escHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getData(root) {
    if (root._taData) return root._taData;
    var el = root.querySelector('[data-ta-data]');
    var data = [];
    if (el) { try { data = JSON.parse(el.textContent); } catch (e) { data = []; } }
    root._taData = Array.isArray(data) ? data : [];
    return root._taData;
  }

  function param(root) {
    return root.getAttribute('data-ta-param') || 'filter.p.m.custom.main_artist';
  }

  /* ----- initialize one instance (lazy, on first interaction) ----- */
  // Keep a hidden <select data-filter-selection> in sync with the active artist so
  // the theme's filters-submit includes it when ANY native filter changes. Without
  // this, changing Product Type / Vinyl Type rebuilds the URL from the form's
  // [data-filter-selection] fields only — which drops the artist, since the artist
  // otherwise lives only in the URL (applied by navigation, not the form).
  function syncHidden(root, gid, name) {
    var sel = root.querySelector('[data-ta-hidden]');
    if (!sel) return;
    sel.innerHTML = '';
    if (gid) {
      var opt = document.createElement('option');
      opt.value = gid;
      opt.textContent = name || gid;
      opt.selected = true;
      sel.appendChild(opt);
    }
  }

  function init(root) {
    if (root._taInit) return;
    root._taInit = true;
    root._taActiveIdx = -1;
    getData(root);

    // Reflect the currently-applied artist (if any) from the URL — authoritative,
    // so it works even for artists outside Shopify's top-100 list.
    var current = new URLSearchParams(window.location.search).get(param(root));
    var match = current ? root._taData.filter(function (a) { return a.gid === current; })[0] : null;
    if (current) {
      var input = root.querySelector('[data-ta-input]');
      var clear = root.querySelector('[data-ta-clear]');
      if (match && input) { input.value = match.name; root._taSelectedName = match.name; }
      if (clear) clear.classList.remove('hidden');
    }
    // Mirror the active artist into the hidden form field (or clear it when none),
    // so native-filter submissions preserve the artist.
    syncHidden(root, current, match ? match.name : '');
  }

  /* ----- render the results list for a query ----- */
  function render(root, q) {
    var data = getData(root);
    var list = root.querySelector('[data-ta-list]');
    if (!list) return;
    q = (q || '').trim().toLowerCase();

    var matches = [];
    for (var i = 0; i < data.length; i++) {
      var name = (data[i].name || '').toLowerCase();
      if (!q || name.indexOf(q) !== -1) {
        matches.push(data[i]);
        if (q && matches.length >= MAX_RESULTS) break;   // cap only when searching
      }
    }

    var shown = matches.slice(0, MAX_RESULTS);
    var total = q ? matches.length : data.length;
    var html = '';

    if (shown.length === 0) {
      html = '<li class="px-3 py-2 text-gray-500">No artists found</li>';
    } else {
      for (var j = 0; j < shown.length; j++) {
        var a = shown[j];
        var label = a.name + (a.count != null ? ' (' + a.count + ')' : '');
        html += '<li role="option" data-ta-option data-gid="' + escHtml(a.gid) + '"' +
          ' class="px-3 py-2 cursor-pointer hover:bg-gray-100">' + escHtml(label) + '</li>';
      }
      if (total > shown.length) {
        html += '<li class="px-3 py-2 text-sm text-gray-400">' +
          '…' + (total - shown.length) + ' more — keep typing</li>';
      }
    }
    list.innerHTML = html;
    root._taActiveIdx = -1;
  }

  function open(root) {
    var input = root.querySelector('[data-ta-input]');
    var list = root.querySelector('[data-ta-list]');
    var q = input ? input.value : '';
    // If the box currently shows the committed selection, open to the FULL list
    // (so the discography "scroll to discover" behavior still works even when an
    // artist is already applied), and select the text so typing replaces it.
    if (input && root._taSelectedName && input.value === root._taSelectedName) {
      q = '';
      try { input.select(); } catch (e) {}
    }
    render(root, q);
    if (list) list.classList.remove('hidden');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function close(root) {
    var list = root.querySelector('[data-ta-list]');
    var input = root.querySelector('[data-ta-input]');
    if (list) list.classList.add('hidden');
    if (input) input.setAttribute('aria-expanded', 'false');
    root._taActiveIdx = -1;
  }

  function closeAll() {
    var roots = document.querySelectorAll('[data-artist-typeahead]');
    for (var i = 0; i < roots.length; i++) close(roots[i]);
  }

  /* ----- navigate: set/clear the artist param, preserve everything else ----- */
  function go(root, gid) {
    var params = new URLSearchParams(window.location.search);
    var p = param(root);
    if (gid) params.set(p, gid); else params.delete(p);
    params.delete('page');   // a new filter selection should start at page 1
    var qs = params.toString();
    window.location.search = qs ? ('?' + qs) : '';
  }

  /* ----- keyboard navigation within the list ----- */
  function move(root, dir) {
    var list = root.querySelector('[data-ta-list]');
    var opts = list ? list.querySelectorAll('[data-ta-option]') : [];
    if (!opts.length) return;
    var idx = (root._taActiveIdx == null ? -1 : root._taActiveIdx) + dir;
    if (idx < 0) idx = opts.length - 1;
    if (idx >= opts.length) idx = 0;
    for (var i = 0; i < opts.length; i++) opts[i].classList.remove('bg-gray-100');
    opts[idx].classList.add('bg-gray-100');
    if (opts[idx].scrollIntoView) opts[idx].scrollIntoView({ block: 'nearest' });
    root._taActiveIdx = idx;
  }

  /* ----- delegated listeners (attached once, to document) ----- */
  document.addEventListener('focusin', function (e) {
    var input = closest(e.target, '[data-ta-input]');
    if (!input) return;
    var root = closest(input, '[data-artist-typeahead]');
    if (!root) return;
    init(root);
    open(root);
  });

  document.addEventListener('input', function (e) {
    var input = closest(e.target, '[data-ta-input]');
    if (!input) return;
    var root = closest(input, '[data-artist-typeahead]');
    if (!root) return;
    var list = root.querySelector('[data-ta-list]');
    if (list) list.classList.remove('hidden');
    render(root, input.value);
  });

  document.addEventListener('click', function (e) {
    var opt = closest(e.target, '[data-ta-option]');
    if (opt) {
      var r1 = closest(opt, '[data-artist-typeahead]');
      if (r1) go(r1, opt.getAttribute('data-gid'));
      return;
    }
    var clear = closest(e.target, '[data-ta-clear]');
    if (clear) {
      var r2 = closest(clear, '[data-artist-typeahead]');
      if (r2) go(r2, null);   // gid=null removes the param
      return;
    }
    // click anywhere outside an open type-ahead closes the lists
    if (!closest(e.target, '[data-artist-typeahead]')) closeAll();
  });

  document.addEventListener('keydown', function (e) {
    var input = closest(e.target, '[data-ta-input]');
    if (!input) return;
    var root = closest(input, '[data-artist-typeahead]');
    if (!root) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); move(root, 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(root, -1); }
    else if (e.key === 'Enter') {
      // Always prevent the Enter keystroke from submitting the surrounding
      // FiltersForm; act on the highlighted option if there is one.
      e.preventDefault();
      var list = root.querySelector('[data-ta-list]');
      var opts = list ? list.querySelectorAll('[data-ta-option]') : [];
      if (root._taActiveIdx >= 0 && opts[root._taActiveIdx]) {
        go(root, opts[root._taActiveIdx].getAttribute('data-gid'));
      }
    } else if (e.key === 'Escape') {
      close(root);
    }
  });

  /* ----- persist the selected artist in the box ON LOAD -----
   * init() reflects the active artist from the URL into the box (name + clear ×).
   * Previously it ran only on focus, so the box looked empty after navigating to a
   * filtered page until you touched it. Run it on load for every instance, and —
   * because the filter markup is cloned from <template> into the live DOM
   * asynchronously by `filters-submit` (and the mobile drawer clones later) —
   * watch for instances appearing and init them then too. init() is idempotent
   * (guards on _taInit), so repeated calls are cheap and safe. */
  function initAll() {
    var roots = document.querySelectorAll('[data-artist-typeahead]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
  window.addEventListener('load', initAll);

  if (window.MutationObserver) {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          // Only react when a type-ahead root itself is added (ignores the list's
          // own <li> re-renders, so this stays cheap during typing).
          var isRoot = n.matches && n.matches('[data-artist-typeahead]');
          var hasRoot = n.querySelector && n.querySelector('[data-artist-typeahead]');
          if (isRoot || hasRoot) { initAll(); return; }
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();